/* eslint-disable no-console */
import camelCaseKeys from 'camelcase-keys'
import { Dispatch } from 'react'

import { createSocket } from '../utils/webSocket'
import { showAlertNotification, updateRunStatus } from './storeActions'
import { Actions } from './storeReducer.types'

// Logging prefix for easy filtering in console
const LOG_PREFIX = '[Sockets]'

const endpoints = {
  runsStatus: '/run_status/',
  alertNotification: '/alert_notification/',
}

let runsStatusTimeoutId: ReturnType<typeof setTimeout> | null = null
let alertNotificationTimeoutId: ReturnType<typeof setTimeout> | null = null

export const createRunsStatusSocket = (dispatch: Dispatch<Actions>) => {
  const socket = createSocket(endpoints.runsStatus)

  if (socket) {
    socket.onmessage = (e: MessageEvent) =>
      updateRunStatus(
        dispatch,
        camelCaseKeys(JSON.parse(e.data), { deep: true })
      )

    socket.onclose = (event) => {
      if (event.code !== 1000) {
        // FIX: Pass dispatch to reconnection via closure
        runsStatusTimeoutId = setTimeout(
          () => createRunsStatusSocket(dispatch),
          10000
        )
      }
    }
  }
  return socket
}

export const closeRunStatusSocket = (socket: WebSocket) => {
  if (runsStatusTimeoutId !== null) {
    clearTimeout(runsStatusTimeoutId)
    runsStatusTimeoutId = null
  }
  if (socket) {
    socket.close(1000)
  }
}

export const createAlertNotificationSocket = (dispatch: Dispatch<Actions>) => {
  console.log(LOG_PREFIX, 'Creating AlertNotification WebSocket', {
    endpoint: endpoints.alertNotification,
    timestamp: new Date().toISOString(),
  })

  const socket = createSocket(endpoints.alertNotification)

  if (socket) {
    console.log(LOG_PREFIX, 'AlertNotification WebSocket created', {
      readyState: socket.readyState,
      url: socket.url,
      timestamp: new Date().toISOString(),
    })

    socket.onmessage = (e: MessageEvent) => {
      const rawData = JSON.parse(e.data)
      const data = camelCaseKeys(rawData, { deep: true })

      console.log(LOG_PREFIX, 'AlertNotification WebSocket message received', {
        eventType: data.eventType,
        status: data.status,
        uploadId: data.uploadId,
        observationsCount: data.observationsId?.length,
        rawData,
        socketReadyState: socket.readyState,
        timestamp: new Date().toISOString(),
      })

      if (
        data.eventType === 'IMAGE_DELETE_UPDATE' &&
        data.status === 'COMPLETED'
      ) {
        window.dispatchEvent(
          new CustomEvent('image-delete-completed', {
            detail: {
              deletedIds: data.deletedIds ?? [],
              skippedIds: data.skippedIds ?? [],
            },
          })
        )
      }

      showAlertNotification(dispatch, data)
    }

    socket.onopen = () => {
      console.log(LOG_PREFIX, 'AlertNotification WebSocket connected', {
        readyState: socket.readyState,
        url: socket.url,
        timestamp: new Date().toISOString(),
      })
    }

    socket.onclose = (event) => {
      console.log(LOG_PREFIX, 'AlertNotification WebSocket closed', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        timestamp: new Date().toISOString(),
      })
      if (event.code !== 1000) {
        console.warn(
          LOG_PREFIX,
          'Unexpected WebSocket close - reconnecting in 10 seconds',
          {
            code: event.code,
            reason: event.reason,
            timestamp: new Date().toISOString(),
          }
        )
        // FIX: Pass dispatch to reconnection via closure
        alertNotificationTimeoutId = setTimeout(
          () => createAlertNotificationSocket(dispatch),
          10000
        )
      }
    }

    socket.onerror = (error) => {
      console.error(LOG_PREFIX, 'AlertNotification WebSocket error', {
        error,
        socketReadyState: socket.readyState,
        timestamp: new Date().toISOString(),
      })
    }
  } else {
    console.error(
      LOG_PREFIX,
      'Failed to create AlertNotification WebSocket - socket is null',
      {
        endpoint: endpoints.alertNotification,
        timestamp: new Date().toISOString(),
      }
    )
  }
  return socket
}

export const closeAlertNotificationSocket = (socket: WebSocket) => {
  if (alertNotificationTimeoutId !== null) {
    clearTimeout(alertNotificationTimeoutId)
    alertNotificationTimeoutId = null
  }
  if (socket) {
    socket.close(1000)
  }
}
